import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * The shared empty / error / no-access state.
 *
 * §1.3 requires every list screen to have empty, loading and error states, and
 * §15 rejects placeholder screens. One component for all three means they are
 * consistent, translated and accessible everywhere rather than improvised per
 * screen.
 */
export function EmptyState({
    icon: Icon,
    title,
    description,
    /** Secondary technical detail — a missing permission, an error reference. */
    detail,
    action,
    className,
    tone = 'neutral',
}: {
    icon?: LucideIcon;
    title: string;
    description?: string;
    detail?: string;
    action?: ReactNode;
    className?: string;
    tone?: 'neutral' | 'destructive';
}) {
    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-14 text-center',
                className,
            )}
            // Announced to screen readers when it replaces loaded content, so a
            // non-sighted user learns the search returned nothing rather than
            // being met with silence.
            role="status"
        >
            {Icon && (
                <div
                    className={cn(
                        'mb-4 flex size-12 items-center justify-center rounded-full',
                        tone === 'destructive'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-muted text-muted-foreground',
                    )}
                >
                    <Icon className="size-6" aria-hidden="true" />
                </div>
            )}

            <p className="text-base font-medium text-balance">{title}</p>

            {description && (
                <p className="text-muted-foreground mt-1.5 max-w-md text-sm text-balance">
                    {description}
                </p>
            )}

            {detail && (
                <code className="text-muted-foreground bg-muted mt-3 rounded px-2 py-1 font-mono text-xs" dir="ltr">
                    {detail}
                </code>
            )}

            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}
